import { Ceil, Floor, Max, Min } from "@pdfking/math"

// WASI/Emscripten errno values
const ENOENT = 44
const EBADF = 8
const EINVAL = 28
const ENOTDIR = 54
const EISDIR = 31

// fcntl commands
const F_DUPFD = 0
const F_GETFD = 1
const F_SETFD = 2
const F_GETFL = 3
const F_SETFL = 4

// open flags
const O_CREAT = 64
const O_TRUNC = 512
const O_APPEND = 1024

// seek whence
const SEEK_SET = 0
const SEEK_CUR = 1
const SEEK_END = 2

type FileEntry = {
  data: Uint8Array
  readonly isDir: boolean
}

type OpenFD = {
  readonly path: string
  flags: number
  offset: number
}

/** Minimal Emscripten FS state — a flat map of path → contents + fd table */
export type MemFS = {
  /** Write a file into the virtual filesystem */
  readonly writeFile: (path: string, data: Uint8Array) => void
  /** Read a file from the virtual filesystem (returns a copy) */
  readonly readFile: (path: string) => Uint8Array
  /** Delete a file from the virtual filesystem */
  readonly unlink: (path: string) => void
  /**
   * Build the syscall import functions for a WASM module.
   * Must be called with the mutable exports proxy so heap access works.
   */
  readonly buildImports: (
    exports: Record<string, unknown>,
    memoryKey: string
  ) => Readonly<Record<string, (...args: number[]) => number | undefined>>
}

const normalizePath = (path: string): string => {
  if (path.charAt(0) === "/") {
    const parts = path.split("/").filter(Boolean)
    const result: string[] = []
    for (const part of parts) {
      if (part === "..") result.pop()
      else if (part !== ".") result.push(part)
    }
    return `/${result.join("/")}`
  }
  return path
}

/**
 * Create a minimal in-memory filesystem that provides Emscripten syscall
 * implementations for WASM modules that need file I/O.
 *
 * Follows Emscripten's FS/SYSCALLS implementation closely.
 */
export const CreateMemFS = (): MemFS => {
  const files = new Map<string, FileEntry>()
  const fds = new Map<number, OpenFD>()
  let nextFd = 10

  // Pre-create directories
  files.set("/tmp", { data: new Uint8Array(0), isDir: true })
  files.set("/dev", { data: new Uint8Array(0), isDir: true })

  const writeFile = (path: string, data: Uint8Array): void => {
    files.set(normalizePath(path), { data: new Uint8Array(data), isDir: false })
  }

  const readFile = (path: string): Uint8Array => {
    const entry = files.get(normalizePath(path))
    if (!entry || entry.isDir) throw new Error(`ENOENT: ${path}`)
    return new Uint8Array(entry.data)
  }

  const unlink = (path: string): void => {
    const p = normalizePath(path)
    if (!files.has(p)) throw new Error(`ENOENT: ${path}`)
    files.delete(p)
  }

  const buildImports = (
    exports: Record<string, unknown>,
    memoryKey: string
  ): Record<string, (...args: number[]) => number | undefined> => {
    const mem = () => (exports[memoryKey] as WebAssembly.Memory).buffer
    const heapU8 = () => new Uint8Array(mem())
    const heapU32 = () => new Uint32Array(mem())
    const heap32 = () => new Int32Array(mem())
    const dv = () => new DataView(mem())

    // UTF8ToString — matches Emscripten's UTF8ToString
    const getStr = (ptr: number): string => {
      const bytes = heapU8()
      let end = ptr
      while (bytes[end] !== 0) end++
      return new TextDecoder().decode(bytes.subarray(ptr, end))
    }

    // SYSCALLS.calculateAt — matches Emscripten
    // dirfd=-100 means AT_FDCWD, uses cwd which is "/"
    const calculateAt = (dirfd: number, path: string): string => {
      if (path.charAt(0) === "/") return normalizePath(path)
      let dir: string
      if (dirfd === -100) {
        dir = "/"
      } else {
        const stream = fds.get(dirfd)
        if (!stream) return normalizePath(`/${path}`)
        dir = stream.path
      }
      if (path.length === 0) return dir
      return normalizePath(`${dir}/${path}`)
    }

    // SYSCALLS.varargs + syscallGetVarargI — matches Emscripten exactly
    // Emscripten: SYSCALLS.varargs = varargs; var ret = HEAP32[+SYSCALLS.varargs>>2]; SYSCALLS.varargs += 4
    let varargs = 0
    const getVarargI = (): number => {
      const ret = heap32()[varargs >> 2]!
      varargs += 4
      return ret
    }

    // SYSCALLS.writeStat — matches Emscripten struct layout exactly
    // Emscripten uses: HEAPU32 for dev/mode/nlink/uid/gid/rdev,
    // HEAP64 for size, HEAP32 for blksize/blocks,
    // HEAP64 for atime/mtime/ctime seconds, HEAPU32 for nanoseconds,
    // HEAP64 for ino
    const writeStat = (buf: number, entry: FileEntry): number => {
      const h = heapU32()
      const d = dv()
      const now = Date.now()
      const timeSec = BigInt(Floor(now / 1000))
      const timeNsec = (now % 1000) * 1000000

      h[buf >> 2] = 1 // st_dev
      h[(buf + 4) >> 2] = entry.isDir ? 0o40755 : 0o100644 // st_mode
      h[(buf + 8) >> 2] = 1 // st_nlink
      h[(buf + 12) >> 2] = 0 // st_uid
      h[(buf + 16) >> 2] = 0 // st_gid
      h[(buf + 20) >> 2] = 0 // st_rdev
      d.setBigInt64(buf + 24, BigInt(entry.data.length), true) // st_size
      heap32()[(buf + 32) >> 2] = 4096 // st_blksize
      heap32()[(buf + 36) >> 2] = Ceil(entry.data.length / 512) // st_blocks
      d.setBigInt64(buf + 40, timeSec, true) // st_atime
      h[(buf + 48) >> 2] = timeNsec // st_atime_nsec
      d.setBigInt64(buf + 56, timeSec, true) // st_mtime
      h[(buf + 64) >> 2] = timeNsec // st_mtime_nsec
      d.setBigInt64(buf + 72, timeSec, true) // st_ctime
      h[(buf + 80) >> 2] = timeNsec // st_ctime_nsec
      d.setBigInt64(buf + 88, BigInt(1), true) // st_ino
      return 0
    }

    return {
      // ___syscall_openat(dirfd, path, flags, varargs)
      // Emscripten: path=SYSCALLS.getStr(path); path=SYSCALLS.calculateAt(dirfd,path);
      //   var mode=varargs?syscallGetVarargI():0; return FS.open(path,flags,mode).fd
      syscall_openat: (
        dirfd: number,
        pathPtr: number,
        flags: number,
        va: number
      ) => {
        varargs = va
        const mode = va ? getVarargI() : 0
        void mode
        const path = calculateAt(dirfd, getStr(pathPtr))
        const entry = files.get(path)

        if (!entry && !(flags & O_CREAT)) return -ENOENT
        if (!entry) {
          files.set(path, { data: new Uint8Array(0), isDir: false })
        } else if (entry.isDir) {
          return -EISDIR
        } else if (flags & O_TRUNC) {
          entry.data = new Uint8Array(0)
        }

        const fd = nextFd++
        fds.set(fd, {
          path,
          flags,
          offset: flags & O_APPEND ? (files.get(path)?.data.length ?? 0) : 0,
        })
        return fd
      },

      // ___syscall_stat64(path, buf)
      // Emscripten: path=SYSCALLS.getStr(path); return SYSCALLS.writeStat(buf,FS.stat(path))
      syscall_stat64: (pathPtr: number, buf: number) => {
        const path = normalizePath(getStr(pathPtr))
        const entry = files.get(path)
        if (!entry) return -ENOENT
        return writeStat(buf, entry)
      },

      // ___syscall_fcntl64(fd, cmd, varargs)
      // Matches Emscripten's switch cases: 0=F_DUPFD, 1,2=return 0, 3=F_GETFL, 4=F_SETFL
      syscall_fcntl64: (fd: number, cmd: number, va: number) => {
        varargs = va
        const openFd = fds.get(fd)
        if (!openFd && fd > 2) return -EBADF

        switch (cmd) {
          case F_DUPFD: {
            let arg = getVarargI()
            if (arg < 0) return -EINVAL
            while (fds.has(arg)) arg++
            if (openFd) fds.set(arg, { ...openFd })
            return arg
          }
          case F_GETFD:
          case F_SETFD:
            return 0
          case F_GETFL:
            return openFd?.flags ?? 0
          case F_SETFL: {
            const arg = getVarargI()
            if (openFd) openFd.flags |= arg
            return 0
          }
          default:
            return -EINVAL
        }
      },

      // ___syscall_ioctl — qpdf doesn't use TTY, return 0
      syscall_ioctl: () => 0,

      // ___syscall_unlinkat(dirfd, path, flags)
      // Emscripten: if(!flags) FS.unlink(path); else if(flags===512) FS.rmdir(path); else return -28
      syscall_unlinkat: (dirfd: number, pathPtr: number, flags: number) => {
        const path = calculateAt(dirfd, getStr(pathPtr))
        const entry = files.get(path)
        if (!entry) return -ENOENT
        if (!flags) {
          if (entry.isDir) return -EISDIR
          files.delete(path)
        } else if (flags === 512) {
          if (!entry.isDir) return -ENOTDIR
          files.delete(path)
        } else {
          return -EINVAL
        }
        return 0
      },

      // ___syscall_renameat(olddirfd, oldpath, newdirfd, newpath)
      // Emscripten: FS.rename(oldpath, newpath)
      syscall_renameat: (
        olddirfd: number,
        oldpathPtr: number,
        newdirfd: number,
        newpathPtr: number
      ) => {
        const oldpath = calculateAt(olddirfd, getStr(oldpathPtr))
        const newpath = calculateAt(newdirfd, getStr(newpathPtr))
        const entry = files.get(oldpath)
        if (!entry) return -ENOENT
        files.set(newpath, entry)
        files.delete(oldpath)
        for (const [, openFd] of fds) {
          if (openFd.path === oldpath) {
            ;(openFd as { path: string }).path = newpath
          }
        }
        return 0
      },

      // _fd_close(fd)
      // Emscripten: var stream=SYSCALLS.getStreamFromFD(fd); FS.close(stream); return 0
      fd_close: (fd: number) => {
        if (!fds.has(fd)) return EBADF
        fds.delete(fd)
        return 0
      },

      // _fd_read(fd, iov, iovcnt, pnum)
      // Matches Emscripten's doReadv: iterate iovecs, read from file, advance offset
      fd_read: (fd: number, iov: number, iovcnt: number, pnum: number) => {
        const openFd = fds.get(fd)
        if (!openFd) return EBADF
        const entry = files.get(openFd.path)
        if (!entry) return EBADF

        const h8 = heapU8()
        const hU32 = heapU32()
        let ret = 0

        // Matches Emscripten doReadv: ptr=HEAPU32[iov>>2]; len=HEAPU32[iov+4>>2]; iov+=8
        for (let i = 0; i < iovcnt; i++) {
          const ptr = hU32[iov >> 2]!
          const len = hU32[(iov + 4) >> 2]!
          iov += 8

          const available = entry.data.length - openFd.offset
          const curr = Min(len, Max(0, available))
          if (curr > 0) {
            h8.set(
              entry.data.subarray(openFd.offset, openFd.offset + curr),
              ptr
            )
            openFd.offset += curr
          }
          ret += curr
          if (curr < len) break
        }

        hU32[pnum >> 2] = ret
        return 0
      },

      // _fd_write(fd, iov, iovcnt, pnum)
      // Matches Emscripten doWritev: iterate iovecs, write to file, advance offset
      fd_write: (fd: number, iov: number, iovcnt: number, pnum: number) => {
        // stdout (1) and stderr (2) — count bytes, discard content
        if (fd === 1 || fd === 2) {
          const hU32 = heapU32()
          let ret = 0
          for (let i = 0; i < iovcnt; i++) {
            const len = hU32[(iov + 4) >> 2]!
            iov += 8
            ret += len
          }
          hU32[pnum >> 2] = ret
          return 0
        }

        const openFd = fds.get(fd)
        if (!openFd) return EBADF
        const entry = files.get(openFd.path)
        if (!entry) return EBADF

        const hU8 = heapU8()
        const hU32 = heapU32()
        let ret = 0

        for (let i = 0; i < iovcnt; i++) {
          const ptr = hU32[iov >> 2]!
          const len = hU32[(iov + 4) >> 2]!
          iov += 8

          const chunk = hU8.slice(ptr, ptr + len)
          const needed = openFd.offset + len
          if (needed > entry.data.length) {
            const newData = new Uint8Array(needed)
            newData.set(entry.data)
            entry.data = newData
          }
          entry.data.set(chunk, openFd.offset)
          openFd.offset += len
          ret += len
        }

        hU32[pnum >> 2] = ret
        return 0
      },

      // _fd_seek(fd, offset, whence, newOffset)
      // Emscripten: offset=bigintToI53Checked(offset); FS.llseek(stream,offset,whence);
      //   HEAP64[newOffset>>3]=BigInt(stream.position)
      fd_seek: (
        fd: number,
        offset: number,
        whence: number,
        newOffsetPtr: number
      ) => {
        const openFd = fds.get(fd)
        if (!openFd) return EBADF
        const entry = files.get(openFd.path)
        if (!entry) return EBADF

        // offset is i64 — may arrive as BigInt, convert to number
        const off = Number(offset)
        if (Number.isNaN(off)) return 61 // EOVERFLOW, matches Emscripten

        let pos: number
        if (whence === SEEK_SET) pos = off
        else if (whence === SEEK_CUR) pos = openFd.offset + off
        else if (whence === SEEK_END) pos = entry.data.length + off
        else return EINVAL

        openFd.offset = pos
        // Emscripten: HEAP64[newOffset>>3] = BigInt(stream.position)
        dv().setBigInt64(newOffsetPtr, BigInt(pos), true)
        return 0
      },
    }
  }

  return { writeFile, readFile, unlink, buildImports }
}
